import { Download, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createQuotePdf, quotePdfFilename } from '../lib/quotePdf';
import type { CompanyProfile, Plan, Quote } from '../types';
import { QuoteDocument } from './QuoteDocument';
import { QuotePdfPages } from './QuotePdfPages';

interface QuotePreviewProps {
  quote: Quote;
  company: CompanyProfile;
  plan: Plan;
  onClose: () => void;
}

export function QuotePreview({ quote, company, plan, onClose }: QuotePreviewProps) {
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [showProviderSignature, setShowProviderSignature] = useState(quote.showProviderSignature === true);
  const [showCustomerSignature, setShowCustomerSignature] = useState(quote.showSignatureSection !== false);

  useEffect(() => {
    let cancelled = false;
    setGeneratingPdf(true);
    setPdfError('');
    const timer = window.setTimeout(() => {
      const documentElement = document.getElementById('quote-document');
      if (!documentElement) return;
      void createQuotePdf(documentElement, quote).then(({ blob, pageCount }) => {
        if (cancelled) return;
        setPdfBlob(blob);
        setPdfPageCount(pageCount);
        setGeneratingPdf(false);
      }).catch((error) => {
        if (cancelled) return;
        setPdfError(error instanceof Error ? error.message : 'สร้าง PDF ไม่สำเร็จ กรุณาลองอีกครั้ง');
        setGeneratingPdf(false);
      });
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [company, plan, quote, showCustomerSignature, showProviderSignature]);

  const downloadPdf = () => {
    if (!pdfBlob || generatingPdf) return;
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = quotePdfFilename(quote);
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
  };

  return (
    <div className="modal-backdrop preview-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="modal preview-modal quote-preview-modal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
        <header className="modal-header no-print">
          <div className="preview-heading"><span className="eyebrow">ตัวอย่างเอกสารจาก PDF จริง</span><h2>{quote.number}</h2><small>{generatingPdf ? 'กำลังอัปเดตตัวอย่าง…' : pdfPageCount ? `${pdfPageCount} หน้า` : ''}</small></div>
          <div className="header-actions">
            {pdfError && <span className="pdf-error" role="alert">{pdfError}</span>}
            <div className="preview-signature-toggles" role="group" aria-label="ช่องลงนามใน PDF">
              <label className="preview-signature-toggle"><input type="checkbox" checked={showProviderSignature} onChange={(event) => setShowProviderSignature(event.target.checked)} /> ผู้เสนอราคา</label>
              <label className="preview-signature-toggle"><input type="checkbox" checked={showCustomerSignature} onChange={(event) => setShowCustomerSignature(event.target.checked)} /> ลูกค้า</label>
            </div>
            <button className="btn primary preview-download" type="button" onClick={downloadPdf} disabled={generatingPdf || !pdfBlob}><Download size={17} /> {generatingPdf ? 'กำลังเตรียม…' : 'ดาวน์โหลด PDF'}</button>
            <button className="icon-btn" type="button" onClick={onClose} aria-label="ปิด"><X size={20} /></button>
          </div>
        </header>
        <div className="quote-pdf-preview-body">
          {generatingPdf && !pdfBlob && <div className="mobile-pdf-message" role="status"><span className="spin" /> กำลังสร้างตัวอย่างจาก PDF จริง…</div>}
          {pdfError && !pdfBlob && <div className="mobile-pdf-message error" role="alert">{pdfError}</div>}
          {pdfBlob && <QuotePdfPages blob={pdfBlob} maxWidth={860} />}
        </div>
        <div className="quote-preview-pdf-source" aria-hidden="true">
          <QuoteDocument id="quote-document" className="pdf-export" quote={quote} company={company} plan={plan} showProviderSignature={showProviderSignature} showCustomerSignature={showCustomerSignature} />
        </div>
      </section>
    </div>
  );
}
